import { useEffect, useState, type ReactNode } from "react";
import { AtSign, Bell, Building2, Mail, Shield, ShieldAlert, Forward, Save, Check, RefreshCw, X } from "lucide-react";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Card, CardBody, CardHeader } from "../components/Card";
import { api, ApiError } from "../lib/api";
import { useMe, type TenantSettings } from "../lib/me";

export default function Settings() {
  const { me, refresh } = useMe();
  const initial = me.tenant!.settings;

  const [draft, setDraft] = useState<TenantSettings>(initial);
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [savedSection, setSavedSection] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Free-text buffer for the ignored-senders textarea. Kept in raw form so
  // commas / newlines / casing survive while the user is typing — we only
  // parse + normalize on save.
  const initialIgnoredText = (initial.ignoredSenders ?? []).join(", ");
  const [ignoredSendersText, setIgnoredSendersText] = useState(initialIgnoredText);

  // Sync draft if `me` refreshes
  useEffect(() => {
    setDraft(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.autoRepliesEnabled, initial.dryRunMode, initial.signature.plainText, initial.escalationDestination.type, initial.escalationDestination.forwardTo]);

  // Re-sync the textarea buffer when the persisted list changes (after save).
  useEffect(() => {
    setIgnoredSendersText(initialIgnoredText);
  }, [initialIgnoredText]);

  const patch = (p: Partial<TenantSettings>) => setDraft((d) => ({ ...d, ...p }));
  const patchSig = (p: Partial<TenantSettings["signature"]>) =>
    setDraft((d) => ({ ...d, signature: { ...d.signature, ...p } }));
  const patchEsc = (p: Partial<TenantSettings["escalationDestination"]>) =>
    setDraft((d) => ({ ...d, escalationDestination: { ...d.escalationDestination, ...p } }));

  const save = async (sectionKey: string, body: Record<string, unknown>) => {
    setError(null);
    setSavingSection(sectionKey);
    try {
      await api.patch("/api/settings", body);
      await refresh();
      setSavedSection(sectionKey);
      setTimeout(() => setSavedSection(null), 1800);
    } catch (err) {
      const e = err as ApiError;
      setError(e.message ?? "Couldn't save.");
    } finally {
      setSavingSection(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">Configure how Triage behaves for your tenant.</p>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* General */}
      <Section
        icon={<Shield className="h-4 w-4" />}
        title="Replies"
        description="Tenant-wide controls for how Triage sends replies."
      >
        <div className="space-y-4">
          <ToggleRow
            label="Auto-replies"
            hint="When ON, the AI sends replies directly to customers for high-confidence cases. When OFF, every reply drafts for your review."
            checked={draft.autoRepliesEnabled}
            onChange={(v) => patch({ autoRepliesEnabled: v })}
          />
          <ToggleRow
            label="Dry-run mode"
            hint="When ON, drafts are stored but never sent — even on approve. Useful while testing categories and replies before going live."
            checked={draft.dryRunMode}
            onChange={(v) => patch({ dryRunMode: v })}
          />
          <div className="grid grid-cols-2 gap-4">
            <NumberInput
              label="Default confidence threshold"
              hint="Below this, cases route to review."
              value={draft.defaultConfidenceThreshold}
              onChange={(v) => patch({ defaultConfidenceThreshold: v })}
            />
            <NumberInput
              label="Default auto-send threshold"
              hint="At or above this, the AI sends without review (when auto-replies is on)."
              value={draft.defaultAutoSendThreshold}
              onChange={(v) => patch({ defaultAutoSendThreshold: v })}
            />
          </div>
          <SaveRow
            saving={savingSection === "replies"}
            saved={savedSection === "replies"}
            onClick={() =>
              save("replies", {
                autoRepliesEnabled: draft.autoRepliesEnabled,
                dryRunMode: draft.dryRunMode,
                defaultConfidenceThreshold: draft.defaultConfidenceThreshold,
                defaultAutoSendThreshold: draft.defaultAutoSendThreshold,
              })
            }
          />
        </div>
      </Section>

      {/* Business context */}
      <Section
        icon={<Building2 className="h-4 w-4" />}
        title="Business context"
        description="Background about your business — applied to every AI-drafted reply for tone and grounding."
      >
        <div className="space-y-3">
          <div>
            <textarea
              rows={6}
              maxLength={3000}
              placeholder={`Example:\nWe're Acme, a B2B SaaS that helps logistics teams track shipments in real time. Our customers are operations managers at mid-size shipping companies. Tone: friendly, concise, never hard-sell. Standard refund policy: 30 days. Support hours: Mon–Fri 9–6 IST. We never share customer data with third parties.`}
              value={draft.businessContext ?? ""}
              onChange={(e) => patch({ businessContext: e.target.value })}
              className="block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
            />
            <p className="mt-1.5 text-xs text-gray-500">
              Describe what your business does, your tone of voice, key policies, and any facts the AI should know about. Used as background context for reply drafting only — not classification or clarification asks. {(draft.businessContext ?? "").length} / 3000 characters.
            </p>
          </div>
          <SaveRow
            saving={savingSection === "business"}
            saved={savedSection === "business"}
            onClick={() => save("business", { businessContext: draft.businessContext ?? "" })}
          />
        </div>
      </Section>

      {/* Signature */}
      <Section
        icon={<Mail className="h-4 w-4" />}
        title="Reply signature"
        description="Appended to every outbound reply."
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900">Plain text</label>
            <textarea
              rows={5}
              value={draft.signature.plainText}
              onChange={(e) => patchSig({ plainText: e.target.value })}
              className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
            />
          </div>
          <ToggleRow
            label="Append automated-response disclaimer"
            hint="Adds a brief note that the reply was sent by an AI assistant. Recommended."
            checked={draft.signature.autoDisclaimerEnabled}
            onChange={(v) => patchSig({ autoDisclaimerEnabled: v })}
          />
          <SaveRow
            saving={savingSection === "signature"}
            saved={savedSection === "signature"}
            onClick={() => save("signature", { signature: draft.signature })}
          />
        </div>
      </Section>

      {/* Escalation */}
      <Section
        icon={<Forward className="h-4 w-4" />}
        title="Escalation destination"
        description="Where unresolved cases go when the AI can't or shouldn't handle them."
      >
        <div className="space-y-4">
          <Radio
            options={[
              { value: "review_queue_only", label: "Review queue only", hint: "Cases sit in the review queue. You handle them from the dashboard." },
              { value: "forward", label: "Forward to an inbox", hint: "Forward the original email + context to a human-monitored inbox." },
              { value: "webhook", label: "Send to a webhook", hint: "POST a JSON payload to a connector. (Requires connector — coming soon.)", disabled: true },
            ]}
            value={draft.escalationDestination.type}
            onChange={(v) => patchEsc({ type: v as TenantSettings["escalationDestination"]["type"] })}
          />
          {draft.escalationDestination.type === "forward" && (
            <Input
              label="Forward to"
              type="email"
              placeholder="support-team@yourcompany.com"
              value={draft.escalationDestination.forwardTo ?? ""}
              onChange={(e) => patchEsc({ forwardTo: e.target.value })}
            />
          )}
          <SaveRow
            saving={savingSection === "escalation"}
            saved={savedSection === "escalation"}
            onClick={() => save("escalation", { escalationDestination: draft.escalationDestination })}
          />
        </div>
      </Section>

      {/* Guardrails */}
      <Section
        icon={<ShieldAlert className="h-4 w-4" />}
        title="Guardrails"
        description="Safety and cost limits on the AI pipeline."
      >
        <div className="space-y-4">
          <ToggleRow
            label="Content filter"
            hint="Screens every inbound for profanity, threats, sexual content, and severe distress before classification. Flagged emails skip the AI pipeline and land in review with a flag — costs ~$0.0002 per email."
            checked={draft.contentFilteringEnabled}
            onChange={(v) => patch({ contentFilteringEnabled: v })}
          />
          <Input
            label="Max AI replies per case"
            type="number"
            min={1}
            max={20}
            value={draft.maxRepliesPerCase}
            onChange={(e) => patch({ maxRepliesPerCase: Math.max(1, Math.min(20, Number(e.target.value) || 1)) })}
            hint="After this many AI replies on a single case, auto-send stops and further drafts queue for review. Prevents runaway loops and caps cost per conversation."
          />
          <div>
            <label className="block text-sm font-medium text-gray-900">Ignore senders</label>
            <textarea
              rows={3}
              placeholder="yourcompany.com, noreply@vendor.com, marketing.example.com"
              value={ignoredSendersText}
              onChange={(e) => setIgnoredSendersText(e.target.value)}
              className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
            />
            <p className="mt-1.5 text-xs text-gray-500">
              Comma- or newline-separated. Bare domain (e.g. <code className="font-mono">yourcompany.com</code>) matches any sender at that domain or its subdomains. Full email matches exactly. Useful when team members forward internal mail to the support inbox — those messages are silently dropped before classification.
            </p>
          </div>
          <SaveRow
            saving={savingSection === "guardrails"}
            saved={savedSection === "guardrails"}
            onClick={() =>
              save("guardrails", {
                contentFilteringEnabled: draft.contentFilteringEnabled,
                maxRepliesPerCase: draft.maxRepliesPerCase,
                ignoredSenders: ignoredSendersText
                  .split(/[,\n]/)
                  .map((s) => s.trim().toLowerCase())
                  .filter(Boolean),
              })
            }
          />
        </div>
      </Section>

      {/* Outbound archive — BCC every outbound to your mailbox */}
      <Section
        icon={<Mail className="h-4 w-4" />}
        title="Archive outbound to my inbox"
        description="Receive a BCC copy of every AI-sent reply so you have a full record in your own mailbox — even if you stop using Triage."
      >
        <OutboundArchiveControl />
      </Section>

      {/* Sender email */}
      <Section
        icon={<AtSign className="h-4 w-4" />}
        title="Sender email (From address)"
        description="Use your own email like support@yourcompany.com instead of the auto-generated one."
      >
        <SenderEmailControl />
      </Section>

      {/* Reply addressing — controls Reply-To behavior */}
      <Section
        icon={<Mail className="h-4 w-4" />}
        title="Reply addressing"
        description="Where customer replies are routed when they hit reply on a Triage message."
      >
        <ReplyAddressingControl />
      </Section>

      {/* Outbound delivery — SES (default) vs BYO SMTP */}
      <Section
        icon={<Forward className="h-4 w-4" />}
        title="Outbound delivery"
        description="Choose how Triage actually sends replies. SES uses our infrastructure with your verified domain; SMTP routes through your own relay (Gmail, Office365, SendGrid, etc.)."
      >
        <OutboundDeliveryControl />
      </Section>

      {/* Daily Slack digest */}
      <Section
        icon={<Bell className="h-4 w-4" />}
        title="Daily Slack digest"
        description="A once-a-day summary of inbox activity posted to a Slack channel — so you know things are working without checking the dashboard."
      >
        <SlackDigestControl />
      </Section>

      {/* Inbound (read-only) */}
      <Section
        title="Inbound address"
        description="The email address customers send mail to. Configured at signup; immutable in v1."
      >
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
          <code className="break-all font-mono text-sm text-gray-900">
            support@{me.tenant!.inboundSubdomain}
          </code>
        </div>
      </Section>
    </div>
  );
}

interface SenderEmailState {
  configured: boolean;
  email?: string;
  status?: "pending" | "verified" | "failed";
}

function SenderEmailControl() {
  const { me, refresh } = useMe();
  const initial: SenderEmailState = me.tenant?.settings.senderEmail
    ? {
      configured: true,
      email: me.tenant.settings.senderEmail,
      status: me.tenant.settings.senderEmailStatus ?? "pending",
    }
    : { configured: false };

  const [state, setState] = useState<SenderEmailState>(initial);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState<"save" | "check" | "resend" | "remove" | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Poll status every 5s while pending so the user sees verification flip.
  useEffect(() => {
    if (state.status !== "pending") return;
    const id = setInterval(() => void check(), 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status]);

  const save = async () => {
    setError(null);
    setBusy("save");
    try {
      const result = await api.put<{ email: string; status: "pending" | "verified" }>(
        "/api/settings/sender-email",
        { email: draft.trim().toLowerCase() },
      );
      setState({ configured: true, email: result.email, status: result.status });
      setDraft("");
      await refresh();
    } catch (err) {
      setError((err as ApiError).message ?? "Couldn't save.");
    } finally {
      setBusy(null);
    }
  };

  const check = async () => {
    setBusy("check");
    try {
      const result = await api.get<SenderEmailState>("/api/settings/sender-email/status");
      setState(result);
      await refresh();
    } catch (err) {
      setError((err as ApiError).message ?? null);
    } finally {
      setBusy(null);
    }
  };

  const resend = async () => {
    setError(null);
    setBusy("resend");
    try {
      await api.post("/api/settings/sender-email/resend");
      setState((s) => ({ ...s, status: "pending" }));
    } catch (err) {
      setError((err as ApiError).message ?? "Resend failed.");
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    setError(null);
    setBusy("remove");
    try {
      await api.del("/api/settings/sender-email");
      setState({ configured: false });
      await refresh();
    } catch (err) {
      setError((err as ApiError).message ?? "Remove failed.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      {state.configured ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <code className="break-all font-mono text-sm text-gray-900">{state.email}</code>
                <StatusBadge status={state.status ?? "pending"} />
              </div>
              {state.status === "pending" && (
                <p className="mt-2 text-sm text-gray-600">
                  We sent a verification email to{" "}
                  <span className="font-medium text-gray-900">{state.email}</span>. Click the link
                  inside to confirm. This page polls automatically; once verified you'll see a
                  green badge.
                </p>
              )}
              {state.status === "verified" && (
                <p className="mt-2 text-sm text-gray-600">
                  Outbound replies will be sent from this address. To improve deliverability, add
                  this DNS TXT record to your domain:{" "}
                  <code className="rounded bg-white px-1 py-0.5 text-xs">
                    v=spf1 include:amazonses.com ~all
                  </code>
                </p>
              )}
            </div>
            <div className="flex shrink-0 gap-2">
              {state.status === "pending" && (
                <>
                  <Button size="sm" variant="secondary" onClick={check} loading={busy === "check"}>
                    <RefreshCw className="h-3.5 w-3.5" /> Check
                  </Button>
                  <Button size="sm" variant="secondary" onClick={resend} loading={busy === "resend"}>
                    Resend
                  </Button>
                </>
              )}
              <Button size="sm" variant="ghost" onClick={remove} loading={busy === "remove"}>
                <X className="h-3.5 w-3.5" /> Remove
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <Input
            label="Sender email"
            type="email"
            placeholder="support@yourcompany.com"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            hint="A verification email will be sent to this address. Click the link to confirm ownership."
          />
          <div className="flex justify-end">
            <Button onClick={save} loading={busy === "save"} disabled={!draft.trim()}>
              <Save className="h-3.5 w-3.5" /> Send verification email
            </Button>
          </div>
        </div>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}

function OutboundArchiveControl() {
  const { me, refresh } = useMe();
  const initial = me.tenant!.settings.outboundArchiveEmail ?? "";
  const [draftEmail, setDraftEmail] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraftEmail(initial);
  }, [initial]);

  const dirty = draftEmail !== initial;
  const enabled = draftEmail.trim().length > 0;
  const looksValid = draftEmail.trim() === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draftEmail.trim());

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.patch("/api/settings", { outboundArchiveEmail: draftEmail.trim() });
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (err) {
      const e = err as ApiError;
      setError(e.message ?? "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <Input
        label="Archive address"
        type="email"
        placeholder="support@yourcompany.com"
        value={draftEmail}
        onChange={(e) => setDraftEmail(e.target.value)}
        hint="Every outbound reply (and clarification email) gets a hidden BCC to this address. Leave empty to disable. Auto-set to your verified sender email when you verify one."
      />
      {!looksValid && (
        <p className="text-xs text-rose-600">Doesn't look like a valid email.</p>
      )}
      {enabled && (
        <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-600">
          <strong className="text-gray-900">Loop-safe:</strong> if this address forwards mail back
          to Triage (e.g. your Gmail forwards to the inbound subdomain), Triage detects the loop
          via a stamped header and drops the message before any AI runs.
        </div>
      )}
      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving || !dirty || !looksValid}>
          {saving ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : saved ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
          {saving ? "Saving" : saved ? "Saved" : "Save"}
        </Button>
        {enabled && !dirty && (
          <span className="text-sm text-gray-500">
            Active — BCC'ing to <code className="font-mono">{initial}</code>
          </span>
        )}
        {!enabled && !dirty && (
          <span className="text-sm text-gray-500">Disabled — no BCC sent</span>
        )}
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
    </div>
  );
}

function OutboundDeliveryControl() {
  const { me, refresh } = useMe();
  const tenant = me.tenant!;
  const initialProvider = tenant.settings.outboundProvider ?? "ses";
  const initialSmtp = tenant.settings.smtp;

  const [provider, setProvider] = useState<"ses" | "smtp">(initialProvider);
  const [host, setHost] = useState(initialSmtp?.host ?? "");
  const [port, setPort] = useState<number>(initialSmtp?.port ?? 587);
  const [encryption, setEncryption] = useState<"starttls" | "tls">(initialSmtp?.encryption ?? "starttls");
  const [username, setUsername] = useState(initialSmtp?.username ?? "");
  const [fromEmail, setFromEmail] = useState(initialSmtp?.fromEmail ?? "");
  const [fromName, setFromName] = useState(initialSmtp?.fromName ?? "");
  const [password, setPassword] = useState("");
  const [passwordConfigured, setPasswordConfigured] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get<{ configured: boolean }>("/api/settings/smtp/password");
        if (!cancelled) setPasswordConfigured(r.configured);
      } catch {
        // ignore — non-critical
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-pair port + encryption when user flips ports — small QoL.
  useEffect(() => {
    if (port === 465) setEncryption("tls");
    else if (port === 587) setEncryption("starttls");
  }, [port]);

  const saveConfig = async () => {
    setError(null);
    setTestResult(null);
    setSaving(true);
    try {
      const payload: Record<string, unknown> = { outboundProvider: provider };
      if (provider === "smtp") {
        payload.smtp = {
          host: host.trim(),
          port,
          encryption,
          username: username.trim(),
          fromEmail: fromEmail.trim().toLowerCase(),
          ...(fromName.trim() ? { fromName: fromName.trim() } : {}),
        };
      }
      await api.patch("/api/settings", payload);
      if (provider === "smtp" && password) {
        await api.put("/api/settings/smtp/password", { password });
        setPassword("");
        setPasswordConfigured(true);
      }
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (err) {
      const e = err as ApiError;
      setError(e.message ?? "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setError(null);
    setTestResult(null);
    setTesting(true);
    try {
      const r = await api.post<{ ok: boolean; sentTo: string }>("/api/settings/smtp/test", {});
      setTestResult({ ok: true, message: `Test email sent to ${r.sentTo}. Check your inbox.` });
      await refresh();
    } catch (err) {
      const e = err as ApiError;
      setTestResult({ ok: false, message: e.message ?? "Test send failed." });
    } finally {
      setTesting(false);
    }
  };

  const clearPassword = async () => {
    if (!confirm("Remove the saved SMTP password? Outbound via SMTP will fail until you set it again.")) return;
    try {
      await api.del("/api/settings/smtp/password");
      setPasswordConfigured(false);
    } catch (err) {
      const e = err as ApiError;
      setError(e.message ?? "Couldn't clear password.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-3">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="radio"
            checked={provider === "ses"}
            onChange={() => setProvider("ses")}
            className="mt-1"
          />
          <div className="text-sm">
            <div className="font-medium text-gray-900">Amazon SES (default)</div>
            <div className="text-xs text-gray-500">Replies sent via our SES setup using your verified sender domain. Requires DNS verification but no extra credentials.</div>
          </div>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="radio"
            checked={provider === "smtp"}
            onChange={() => setProvider("smtp")}
            className="mt-1"
          />
          <div className="text-sm">
            <div className="font-medium text-gray-900">SMTP relay (bring your own)</div>
            <div className="text-xs text-gray-500">Connect Triage to your own SMTP host — Gmail, Office365, SendGrid, Mailgun, Postmark, or any standards-compliant relay. Reputation is yours.</div>
          </div>
        </label>
      </div>

      {provider === "smtp" && (
        <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <Input
                label="SMTP host"
                placeholder="smtp.office365.com"
                value={host}
                onChange={(e) => setHost(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Port</label>
              <select
                value={port}
                onChange={(e) => setPort(parseInt(e.target.value, 10))}
                className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm"
              >
                <option value={587}>587 (STARTTLS)</option>
                <option value={465}>465 (TLS)</option>
              </select>
            </div>
          </div>
          <Input
            label="Username"
            placeholder="apikey or full email address"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Password {passwordConfigured && <span className="text-xs font-normal text-emerald-700">— saved</span>}
            </label>
            <div className="flex gap-2">
              <input
                type="password"
                placeholder={passwordConfigured ? "•••••••••••• (leave blank to keep)" : "SMTP password or API key"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex-1 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm"
              />
              {passwordConfigured && (
                <Button type="button" variant="secondary" size="sm" onClick={clearPassword}>
                  Clear
                </Button>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Stored encrypted with KMS. We never display or log this value after save.
              {" "}For Gmail use an app password; for Office365 use an app password or SMTP AUTH secret.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="From email"
              type="email"
              placeholder="support@yourcompany.com"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
            />
            <Input
              label="From name (optional)"
              placeholder={tenant.displayName}
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
            />
          </div>
          <p className="text-xs text-gray-500">
            Most SMTP providers require the From email to match the authenticated account.
          </p>
          {tenant.settings.smtp?.lastVerifiedAt && (
            <p className="text-xs text-emerald-700">
              Last test send succeeded: {new Date(tenant.settings.smtp.lastVerifiedAt).toLocaleString()}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={saveConfig} disabled={saving} size="sm">
          {saving ? "Saving…" : saved ? (<><Check className="mr-1 h-4 w-4" /> Saved</>) : (<><Save className="mr-1 h-4 w-4" /> Save</>)}
        </Button>
        {provider === "smtp" && (
          <Button onClick={sendTest} disabled={testing || !passwordConfigured} variant="secondary" size="sm">
            {testing ? "Sending…" : "Send test email"}
          </Button>
        )}
        {provider === "smtp" && !passwordConfigured && (
          <span className="text-xs text-gray-500">Set a password first to enable test send.</span>
        )}
      </div>

      {testResult && (
        <div className={`rounded-md px-3 py-2 text-sm ${testResult.ok ? "border border-emerald-200 bg-emerald-50 text-emerald-800" : "border border-rose-200 bg-rose-50 text-rose-700"}`}>
          {testResult.message}
        </div>
      )}
      {error && <p className="text-sm text-rose-600">{error}</p>}
    </div>
  );
}

function ReplyAddressingControl() {
  const { me, refresh } = useMe();
  const tenant = me.tenant!;
  const senderVerified =
    !!tenant.settings.senderEmail && tenant.settings.senderEmailStatus === "verified";
  const inboundEmail = `support@${tenant.inboundSubdomain}`;

  const initial = tenant.settings.replyAddressMode ?? "triagent_subdomain";
  const [mode, setMode] = useState<"triagent_subdomain" | "verified_sender">(initial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMode(tenant.settings.replyAddressMode ?? "triagent_subdomain");
  }, [tenant.settings.replyAddressMode]);

  const dirty = mode !== initial;

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.patch("/api/settings", { replyAddressMode: mode });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
      await refresh();
    } catch (err) {
      setError((err as ApiError).message ?? "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 p-3 hover:bg-gray-50">
          <input
            type="radio"
            checked={mode === "triagent_subdomain"}
            onChange={() => setMode("triagent_subdomain")}
            className="mt-1 h-4 w-4"
          />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">
              Triage subdomain (default)
            </p>
            <p className="mt-0.5 text-sm text-gray-500">
              Replies use{" "}
              <code className="rounded bg-gray-100 px-1 text-xs">
                support+case-X@{tenant.inboundSubdomain}
              </code>{" "}
              as Reply-To. Customers' replies route directly to Triage — no forwarding setup
              needed. They will see the appgambit domain when they reply.
            </p>
          </div>
        </label>

        <label
          className={
            "flex cursor-pointer items-start gap-3 rounded-md border p-3 " +
            (senderVerified
              ? "border-gray-200 hover:bg-gray-50"
              : "cursor-not-allowed border-gray-200 bg-gray-50 opacity-60")
          }
        >
          <input
            type="radio"
            checked={mode === "verified_sender"}
            onChange={() => setMode("verified_sender")}
            disabled={!senderVerified}
            className="mt-1 h-4 w-4"
          />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">
              Use my verified sender (no Triage visibility)
              {!senderVerified && (
                <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                  Verify a sender email first
                </span>
              )}
            </p>
            <p className="mt-0.5 text-sm text-gray-500">
              Both From and Reply-To use{" "}
              <code className="rounded bg-gray-100 px-1 text-xs">
                {tenant.settings.senderEmail ?? "support@yourcompany.com"}
              </code>
              . Customers never see the appgambit domain. Requires a forwarding rule from your
              support inbox to Triage (instructions below).
            </p>
          </div>
        </label>
      </div>

      {mode === "verified_sender" && senderVerified && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">Forwarding setup required</p>
          <p className="mt-1 text-sm text-amber-800">
            For customers to reach Triage when they email{" "}
            <code className="rounded bg-white px-1 text-xs">{tenant.settings.senderEmail}</code>,
            forward all mail from that address to:
          </p>
          <code className="mt-2 block break-all rounded-md border border-amber-300 bg-white px-3 py-2 font-mono text-sm">
            {inboundEmail}
          </code>
          <ol className="mt-3 list-inside list-decimal space-y-1 text-sm text-amber-800">
            <li>
              In your email provider (Google Workspace, Microsoft 365, custom mail server) set up
              a forwarding rule: <strong>{tenant.settings.senderEmail}</strong> → <strong>{inboundEmail}</strong>.
            </li>
            <li>
              Use a <em>plain SMTP forward</em> — not a redirect that rewrites headers. Standard
              forwarding rules in Workspace / 365 work.
            </li>
            <li>
              For best deliverability, add{" "}
              <code className="rounded bg-white px-1 text-xs">include:amazonses.com</code> to your
              SPF record on the sender domain. Without it, replies may land in spam.
            </li>
          </ol>
          <p className="mt-3 text-sm text-amber-800">
            Threading: customer replies preserve <code>In-Reply-To</code> headers through the
            forward, so cases continue correctly.
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {saved && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
            <Check className="h-3.5 w-3.5" /> Saved
          </span>
        )}
        <Button size="sm" onClick={save} loading={saving} disabled={!dirty}>
          <Save className="h-3.5 w-3.5" /> Save
        </Button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: "pending" | "verified" | "failed" }) {
  const cls =
    status === "verified"
      ? "bg-emerald-100 text-emerald-700"
      : status === "failed"
        ? "bg-rose-100 text-rose-700"
        : "bg-amber-100 text-amber-700";
  const label = status === "verified" ? "Verified" : status === "failed" ? "Failed" : "Pending verification";
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

/* ── small components ───────────────────────────────────────────────── */

function Section({
  icon,
  title,
  description,
  children,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            {icon && <span className="text-gray-400">{icon}</span>}
            {title}
          </span>
        }
        description={description}
      />
      <CardBody>{children}</CardBody>
    </Card>
  );
}

const TIMEZONE_OPTIONS: Array<{ group: string; tzs: string[] }> = [
  { group: "UTC", tzs: ["UTC"] },
  {
    group: "Asia",
    tzs: [
      "Asia/Kolkata", "Asia/Karachi", "Asia/Dhaka", "Asia/Bangkok", "Asia/Jakarta",
      "Asia/Singapore", "Asia/Hong_Kong", "Asia/Shanghai", "Asia/Tokyo", "Asia/Seoul",
      "Asia/Manila", "Asia/Dubai", "Asia/Tehran", "Asia/Jerusalem",
    ],
  },
  {
    group: "Europe",
    tzs: [
      "Europe/London", "Europe/Dublin", "Europe/Lisbon", "Europe/Paris", "Europe/Berlin",
      "Europe/Madrid", "Europe/Rome", "Europe/Amsterdam", "Europe/Zurich",
      "Europe/Stockholm", "Europe/Athens", "Europe/Helsinki", "Europe/Warsaw",
      "Europe/Istanbul", "Europe/Moscow",
    ],
  },
  {
    group: "Americas",
    tzs: [
      "America/St_Johns", "America/Halifax", "America/New_York", "America/Toronto",
      "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles",
      "America/Vancouver", "America/Anchorage", "Pacific/Honolulu",
      "America/Mexico_City", "America/Bogota", "America/Lima", "America/Sao_Paulo",
      "America/Buenos_Aires", "America/Santiago",
    ],
  },
  {
    group: "Africa",
    tzs: ["Africa/Casablanca", "Africa/Lagos", "Africa/Cairo", "Africa/Nairobi", "Africa/Johannesburg"],
  },
  {
    group: "Oceania",
    tzs: ["Australia/Perth", "Australia/Adelaide", "Australia/Brisbane", "Australia/Sydney", "Pacific/Auckland"],
  },
];

function hourLabel(h: number): string {
  const period = h < 12 ? "AM" : "PM";
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${display}:00 ${period}`;
}

function SlackDigestControl() {
  const { me, refresh } = useMe();
  const initial = me.tenant!.settings;

  const browserTz = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  })();

  const [webhookUrl, setWebhookUrl] = useState(initial.slackWebhookUrl ?? "");
  const [enabled, setEnabled] = useState(initial.slackDailyDigestEnabled ?? false);
  const [hour, setHour] = useState(initial.slackDigestHourLocal ?? 9);
  const [timezone, setTimezone] = useState(initial.slackDigestTimezone ?? browserTz);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testMessage, setTestMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    setWebhookUrl(initial.slackWebhookUrl ?? "");
    setEnabled(initial.slackDailyDigestEnabled ?? false);
    setHour(initial.slackDigestHourLocal ?? 9);
    setTimezone(initial.slackDigestTimezone ?? browserTz);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initial.slackWebhookUrl,
    initial.slackDailyDigestEnabled,
    initial.slackDigestHourLocal,
    initial.slackDigestTimezone,
  ]);

  const webhookValid = webhookUrl === "" || /^https:\/\/hooks\.slack\.com\/services\//i.test(webhookUrl);

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.patch("/api/settings", {
        slackWebhookUrl: webhookUrl.trim(),
        slackDailyDigestEnabled: enabled,
        slackDigestHourLocal: hour,
        slackDigestTimezone: timezone,
      });
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      const e = err as ApiError;
      setError(e.message ?? "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTestMessage(null);
    setTesting(true);
    try {
      await api.post("/api/settings/slack/test", {});
      setTestMessage({ ok: true, text: "Test sent — check your Slack channel." });
    } catch (err) {
      const e = err as ApiError;
      setTestMessage({ ok: false, text: e.message ?? "Send failed." });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-900">Incoming webhook URL</label>
        <input
          type="text"
          placeholder="https://hooks.slack.com/services/..."
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
        />
        <p className="mt-1.5 text-xs text-gray-500">
          Create one at <code className="font-mono">api.slack.com/apps</code> → Incoming Webhooks. Paste the URL above.
        </p>
        {!webhookValid && (
          <p className="mt-1 text-xs text-rose-600">
            Doesn't look like a Slack incoming webhook URL.
          </p>
        )}
      </div>

      <ToggleRow
        label="Enable daily digest"
        hint="When on, a digest is sent every day at the time below."
        checked={enabled}
        onChange={setEnabled}
      />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-900">Send at</label>
          <select
            value={hour}
            onChange={(e) => setHour(Number(e.target.value))}
            className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>{hourLabel(h)}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-900">Timezone</label>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="mt-1.5 block w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
          >
            {TIMEZONE_OPTIONS.map((g) => (
              <optgroup key={g.group} label={g.group}>
                {g.tzs.map((tz) => (
                  <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </div>
      <p className="text-xs text-gray-500">
        The digest covers the last 24 hours of inbox activity. Browser thinks your timezone is <code className="font-mono">{browserTz}</code>.
      </p>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving || !webhookValid}>
          {saving ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : saved ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
          {saving ? "Saving" : saved ? "Saved" : "Save"}
        </Button>
        <Button
          variant="secondary"
          onClick={sendTest}
          disabled={testing || !webhookValid || webhookUrl.trim() === ""}
        >
          {testing ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
          Send test now
        </Button>
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {testMessage && (
        <p className={"text-sm " + (testMessage.ok ? "text-emerald-700" : "text-rose-600")}>
          {testMessage.text}
        </p>
      )}
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {hint && <p className="mt-0.5 text-sm text-gray-500">{hint}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className="mt-0.5 shrink-0"
        aria-pressed={checked}
      >
        <span
          className={
            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors " +
            (checked ? "bg-gray-900" : "bg-gray-300")
          }
        >
          <span
            className={
              "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform " +
              (checked ? "translate-x-4" : "translate-x-0.5")
            }
          />
        </span>
      </button>
    </div>
  );
}

function NumberInput({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <Input
      label={label}
      type="number"
      step="0.05"
      min={0}
      max={1}
      value={value}
      onChange={(e) => {
        const n = Number(e.target.value);
        if (!Number.isNaN(n)) onChange(Math.max(0, Math.min(1, n)));
      }}
      hint={hint}
    />
  );
}

function Radio({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: string; label: string; hint?: string; disabled?: boolean }>;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      {options.map((o) => (
        <label
          key={o.value}
          className={
            "flex items-start gap-3 rounded-md border p-3 " +
            (o.disabled
              ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-60"
              : "cursor-pointer border-gray-200 hover:bg-gray-50")
          }
        >
          <input
            type="radio"
            checked={value === o.value}
            disabled={o.disabled}
            onChange={() => onChange(o.value)}
            className="mt-1 h-4 w-4"
          />
          <div>
            <p className="text-sm font-medium text-gray-900">{o.label}</p>
            {o.hint && <p className="mt-0.5 text-sm text-gray-500">{o.hint}</p>}
          </div>
        </label>
      ))}
    </div>
  );
}

function SaveRow({
  saving,
  saved,
  onClick,
}: {
  saving: boolean;
  saved: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex items-center justify-end gap-3 pt-1">
      {saved && (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
          <Check className="h-3.5 w-3.5" /> Saved
        </span>
      )}
      <Button onClick={onClick} loading={saving} size="sm">
        <Save className="h-3.5 w-3.5" /> Save
      </Button>
    </div>
  );
}
